import { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'wouter';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  FileText, 
  Settings, 
  ChevronDown,
  Home,
  Wrench,
  BarChart3,
  Clock,
  Receipt,
  Shield,
  Building,
  CreditCard,
  Phone,
  Mail,
  Menu,
  X,
  UserPlus,
  MapPin,
  Contact,
  Truck,
  Lightbulb,
  TrendingUp,
  CalendarDays
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/shared/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/shared/components/ui/navigation-menu';
import { Badge } from '@/shared/components/ui/badge';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBranding } from '@/shared/contexts/BrandingContext';
import type { User } from '@shared/schema/types';

interface NavigationItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  roles?: string[];
  brands?: string[];
  badge?: string;
}

interface NavigationGroup {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavigationItem[];
  roles?: string[];
  brands?: string[];
}

// Define role-based navigation structure
const navigationGroups: NavigationGroup[] = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { 
        title: 'My Dashboard', 
        href: '/dashboard', 
        icon: LayoutDashboard,
        description: 'Personal dashboard overview'
      },
      { 
        title: 'Business Intelligence', 
        href: '/command-center', 
        icon: BarChart3,
        description: 'Executive metrics and analytics',
        roles: ['business_owner'],
        brands: ['greenshield', 'aegis360']
      },
      { 
        title: 'Admin Dashboard', 
        href: '/admin/command-center', 
        icon: Shield,
        description: 'System administration',
        roles: ['admin', 'business_owner'],
        brands: ['aegis360']
      }
    ]
  },
  {
    title: 'Work Management',
    icon: Calendar,
    items: [
      { 
        title: 'My Schedule', 
        href: '/predictive-calendar', 
        icon: Calendar,
        description: 'View and manage appointments',
        roles: ['technician', 'business_owner']
      },
      { 
        title: 'Service Orders', 
        href: '/services', 
        icon: Wrench,
        description: 'Manage service requests and orders',
        roles: ['technician', 'business_owner']
      },
      { 
        title: 'Assignments', 
        href: '/field-technician', 
        icon: Clock,
        description: 'Daily assignments and tasks',
        roles: ['technician']
      }
    ]
  },
  {
    title: 'Customer Management',
    icon: Users,
    items: [
      { 
        title: 'Customer Database', 
        href: '/customer-management', 
        icon: Users,
        description: 'Manage customer information'
      },
      { 
        title: 'Quotes & Pricing', 
        href: '/quotation', 
        icon: FileText,
        description: 'Create and manage quotes',
        roles: ['business_owner', 'admin']
      },
      { 
        title: 'Invoices', 
        href: '/customer-management?tab=invoices', 
        icon: Receipt,
        description: 'Invoice management and billing',
        roles: ['business_owner', 'admin']
      }
    ]
  },
  {
    title: 'CRM & Relationships',
    icon: Building,
    brands: ['greenshield', 'aegis360'],
    roles: ['business_owner', 'admin'],
    items: [
      { 
        title: 'CRM Dashboard', 
        href: '/crm', 
        icon: BarChart3,
        description: 'Customer & supplier relationship overview'
      },
      { 
        title: 'Customer Database', 
        href: '/crm/customers', 
        icon: Users,
        description: 'Comprehensive customer management'
      },
      { 
        title: 'Supplier Management', 
        href: '/crm/suppliers', 
        icon: Truck,
        description: 'Supply chain and vendor relationships'
      },
      { 
        title: 'Contact Directory', 
        href: '/crm/contacts', 
        icon: Contact,
        description: 'Individual contact management'
      },
      { 
        title: 'Address Management', 
        href: '/crm/addresses', 
        icon: MapPin,
        description: 'Property and location management'
      }
    ]
  },
  {
    title: 'Business Tools',
    icon: Building,
    brands: ['greenshield', 'aegis360'],
    roles: ['business_owner', 'admin'],
    items: [
      { 
        title: 'Marketing Strategy', 
        href: '/marketing-strategy', 
        icon: TrendingUp,
        description: 'Comprehensive marketing strategies and insights'
      },
      { 
        title: 'Marketing Calendar', 
        href: '/marketing-calendar', 
        icon: CalendarDays,
        description: 'Plan and manage marketing campaigns and events'
      },
      { 
        title: 'Marketing Dashboard', 
        href: '/marketing-dashboard', 
        icon: BarChart3,
        description: 'Track marketing performance and campaign metrics'
      },
      { 
        title: 'Tender Management', 
        href: '/tenders', 
        icon: FileText,
        description: 'Commercial contract management',
        brands: ['greenshield']
      },
      { 
        title: 'Forms & Reports', 
        href: '/forms-hub', 
        icon: FileText,
        description: 'Digital documentation hub'
      },
      { 
        title: 'Analytics Dashboard', 
        href: '/analytics-dashboard', 
        icon: BarChart3,
        description: 'Performance metrics and insights'
      },
      { 
        title: 'Pricing Management', 
        href: '/pricing-admin', 
        icon: CreditCard,
        description: 'Dynamic pricing and plans'
      }
    ]
  },
  {
    title: 'System & Settings',
    icon: Settings,
    roles: ['business_owner', 'admin'],
    items: [
      { 
        title: 'User Management', 
        href: '/access-control', 
        icon: Users,
        description: 'Manage user access and roles'
      },
      { 
        title: 'System Admin', 
        href: '/backend-admin', 
        icon: Shield,
        description: 'System administration dashboard'
      },
      { 
        title: 'IP Management', 
        href: '/ip-management', 
        icon: Lightbulb,
        description: 'Intellectual property and patent management'
      },
      { 
        title: 'Profile Settings', 
        href: '/profile', 
        icon: Settings,
        description: 'Account and company settings'
      },
      { 
        title: 'Notifications', 
        href: '/notification-preferences', 
        icon: Mail,
        description: 'Configure alerts and notifications'
      }
    ]
  }
];

// Quick actions for authenticated users
const quickActions = [
  { 
    title: 'New Service Order', 
    href: '/services?action=create', 
    icon: Wrench,
    roles: ['technician', 'business_owner'] 
  },
  { 
    title: 'Add Customer', 
    href: '/customer-management?action=create', 
    icon: Users,
    roles: ['business_owner', 'admin'] 
  },
  { 
    title: 'Add Supplier', 
    href: '/crm/suppliers?action=create', 
    icon: Truck,
    roles: ['business_owner', 'admin'] 
  },
  { 
    title: 'Create Quote', 
    href: '/quotation?action=create', 
    icon: FileText,
    roles: ['business_owner', 'admin'] 
  },
  { 
    title: 'Emergency Call', 
    href: 'tel:07913024492', 
    icon: Phone,
    brands: ['greenshield'] 
  }
];

export default function AppNavigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroups, setOpenMobileGroups] = useState<string[]>(['Dashboard']);
  const [location] = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { currentBrand, isAegis360, isGreenshield } = useBranding();

  // Helper function to check if user has access to an item
  const hasAccess = (item: NavigationItem | NavigationGroup) => {
    // Check brand access
    if (item.brands && !item.brands.includes(currentBrand.type)) {
      return false;
    }
    
    // Check role access for authenticated users
    if (isAuthenticated && user && item.roles) {
      const typedUser = user as User;
      return item.roles.includes(typedUser.role);
    }
    
    // If no role restrictions but user is not authenticated, hide business-related items
    if (!item.roles) {
      // Only show basic items to unauthenticated users
      const publicItems = ['Home', 'Services', 'Contact', 'Pricing'];
      if (!isAuthenticated && !publicItems.includes(item.title)) {
        return false;
      }
      return true;
    }
    
    // Default: no access if role restrictions exist but user not authenticated
    return false;
  };

  // Memoized filtering for better performance - only recalculate when dependencies change
  const filteredGroups = useMemo(() => {
    return navigationGroups
      .filter(hasAccess)
      .map(group => ({
        ...group,
        items: group.items.filter(hasAccess)
      }))
      .filter(group => group.items.length > 0);
  }, [currentBrand.type, isAuthenticated, user]);

  const filteredQuickActions = useMemo(() => {
    return quickActions.filter(hasAccess);
  }, [currentBrand.type, isAuthenticated, user]);

  // Optimized toggle mobile group with useCallback for stable reference
  const toggleMobileGroup = useCallback((groupTitle: string) => {
    setOpenMobileGroups(prev =>
      prev.includes(groupTitle)
        ? prev.filter(g => g !== groupTitle)
        : [...prev, groupTitle]
    );
  }, []);

  // Optimized mobile menu close handler
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Helper function to check if URL is internal and should use SPA routing
  const isInternalHref = (href: string) => {
    // Skip external protocols
    if (href.startsWith('tel:') || href.startsWith('mailto:') || href.startsWith('http')) {
      return false;
    }
    // Only internal paths starting with '/' or relative paths
    return href.startsWith('/') || !href.includes(':');
  };

  // Helper function to preserve brand parameter during navigation (internal links only)
  const preserveBrandUrl = (href: string) => {
    // Skip brand preservation for external links
    if (!isInternalHref(href)) {
      return href;
    }

    const currentParams = new URLSearchParams(window.location.search);
    const brandParam = currentParams.get('brand');
    
    if (brandParam) {
      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('brand', brandParam);
        return url.pathname + url.search;
      } catch {
        // Fallback for relative URLs
        const separator = href.includes('?') ? '&' : '?';
        return `${href}${separator}brand=${encodeURIComponent(brandParam)}`;
      }
    }
    
    return href;
  };

  // Check if current path is active
  const isActivePath = (href: string) => {
    if (href === '/dashboard' && location === '/') return true;
    return location === href || location.startsWith(href + '/');
  };

  // Brand context display
  const BrandContext = () => {
    const userRole = isAuthenticated && user ? (user as User).role : null;
    
    return (
      <div className="flex items-center space-x-2">
        <Badge 
          variant={isAegis360 ? "default" : "secondary"}
          className={`text-xs ${
            isAegis360 
              ? 'bg-blue-600 text-white' 
              : 'bg-green-600 text-white'
          }`}
          data-testid="brand-context"
        >
          {currentBrand.name}
        </Badge>
        {userRole && (
          <Badge variant="outline" className="text-xs" data-testid="role-context">
            {userRole.replace('_', ' ').toUpperCase()}
          </Badge>
        )}
      </div>
    );
  };

  // Desktop Navigation Menu
  const DesktopNavigation = () => (
    <div className="hidden lg:flex items-center space-x-6">
      <NavigationMenu>
        <NavigationMenuList>
          {/* Home */}
          <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link href={preserveBrandUrl('/')} className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
                location === '/' ? 'text-primary' : 'text-muted-foreground'
              }`}>
                <Home className="h-4 w-4 inline mr-2" />
                Home
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>

          {/* Dynamic Navigation Groups */}
          {filteredGroups.map((group) => (
            <NavigationMenuItem key={group.title}>
              <NavigationMenuTrigger className="text-sm font-medium">
                <group.icon className="h-4 w-4 mr-2" />
                {group.title}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="p-6 w-[400px]">
                  <div className="grid gap-3">
                    {group.items.map((item) => (
                      <NavigationMenuLink key={item.href} asChild>
                        <Link
                          href={preserveBrandUrl(item.href)}
                          className={`block p-3 rounded-lg hover:bg-accent transition-colors ${
                            isActivePath(item.href) ? 'bg-accent' : ''
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <item.icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{item.title}</span>
                                {item.badge && (
                                  <Badge variant="secondary" className="text-xs">
                                    {item.badge}
                                  </Badge>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    ))}
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}

          {/* Quick Actions Dropdown */}
          {isAuthenticated && filteredQuickActions.length > 0 && (
            <NavigationMenuItem>
              <NavigationMenuTrigger className="text-sm font-medium">
                Quick Actions
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="p-4 w-[300px]">
                  <div className="grid gap-2">
                    {filteredQuickActions.map((action) => (
                      isInternalHref(action.href) ? (
                        <NavigationMenuLink key={action.href} asChild>
                          <Link
                            href={preserveBrandUrl(action.href)}
                            className="block p-3 rounded-lg hover:bg-accent transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <action.icon className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{action.title}</span>
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      ) : (
                        <a
                          key={action.href}
                          href={action.href}
                          className="block p-3 rounded-lg hover:bg-accent transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <action.icon className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{action.title}</span>
                          </div>
                        </a>
                      )
                    ))}
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          )}
        </NavigationMenuList>
      </NavigationMenu>

      <BrandContext />
    </div>
  );

  // Mobile Navigation Menu
  const MobileNavigation = () => (
    <div className="lg:hidden">
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-10 w-10"
            data-testid="mobile-menu-trigger"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle navigation menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[300px] sm:w-[400px]">
          <div className="flex items-center justify-between mb-6">
            <BrandContext />
            <Button
              variant="ghost"
              size="icon"
              onClick={closeMobileMenu}
              data-testid="mobile-menu-close"
              className="transition-transform active:scale-95"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="space-y-6">
            {/* Home Link */}
            <Link href={preserveBrandUrl('/')} onClick={closeMobileMenu}>
              <div className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 active:scale-[0.98] ${
                location === '/' ? 'bg-accent' : 'hover:bg-accent'
              }`} style={{ willChange: 'transform, background-color' }}>
                <Home className="h-5 w-5" />
                <span className="font-medium">Home</span>
              </div>
            </Link>

            {/* Navigation Groups */}
            {filteredGroups.map((group) => {
              const isGroupOpen = openMobileGroups.includes(group.title);
              return (
                <div key={group.title} className="space-y-2">
                  <button
                    onClick={() => toggleMobileGroup(group.title)}
                    className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-accent rounded-lg transition-all duration-200 active:scale-[0.98]"
                    style={{ willChange: 'transform, background-color' }}
                    data-testid={`mobile-group-${group.title.toLowerCase().replace(' ', '-')}`}
                  >
                    <div className="flex items-center space-x-2">
                      <group.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        {group.title}
                      </span>
                    </div>
                    <ChevronDown 
                      className="h-4 w-4 text-muted-foreground transition-transform duration-300 ease-out"
                      style={{ 
                        transform: isGroupOpen ? 'rotate3d(0, 0, 1, 180deg)' : 'rotate3d(0, 0, 1, 0deg)',
                        willChange: 'transform'
                      }}
                    />
                  </button>
                  {isGroupOpen && (
                    <div className="space-y-1 ml-6">
                      {group.items.map((item) => (
                        <Link 
                          key={item.href} 
                          href={preserveBrandUrl(item.href)}
                          onClick={closeMobileMenu}
                        >
                          <div className={`flex items-center space-x-3 p-2 rounded-lg transition-all duration-200 active:scale-[0.98] ${
                            isActivePath(item.href) ? 'bg-accent' : 'hover:bg-accent'
                          }`} style={{ willChange: 'transform, background-color' }}>
                            <item.icon className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{item.title}</span>
                            {item.badge && (
                              <Badge variant="secondary" className="text-xs ml-auto">
                                {item.badge}
                              </Badge>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Quick Actions */}
            {isAuthenticated && filteredQuickActions.length > 0 && (
              <div className="space-y-2 pt-4 border-t">
                <div className="px-3 py-2">
                  <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Quick Actions
                  </span>
                </div>
                <div className="space-y-1">
                  {filteredQuickActions.map((action) => (
                    isInternalHref(action.href) ? (
                      <Link 
                        key={action.href} 
                        href={preserveBrandUrl(action.href)}
                        onClick={closeMobileMenu}
                      >
                        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-accent transition-all duration-200 active:scale-[0.98]" 
                             style={{ willChange: 'transform, background-color' }}>
                          <action.icon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{action.title}</span>
                        </div>
                      </Link>
                    ) : (
                      <a
                        key={action.href}
                        href={action.href}
                        onClick={closeMobileMenu}
                        className="block"
                      >
                        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-accent transition-all duration-200 active:scale-[0.98]"
                             style={{ willChange: 'transform, background-color' }}>
                          <action.icon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{action.title}</span>
                        </div>
                      </a>
                    )
                  ))}
                </div>
              </div>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );

  return (
    <nav className="flex items-center justify-between w-full" data-testid="app-navigation">
      <DesktopNavigation />
      <MobileNavigation />
    </nav>
  );
}